import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  CalendarDaysIcon,
  ArrowDownTrayIcon,
  ChevronDownIcon,
  XMarkIcon,
  ClockIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import Button from '../ui/Button';
import DateInput from '../ui/DateInput';
import DropdownPanel from '../ui/DropdownPanel';
import VendorFilterModal from './VendorFilterModal';

function formatInputDate(date) {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseInputDate(value) {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function StateDistributionFilters({
  labels = {},
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  vendorId = '',
  onVendorChange,
  vendors = [],
  showVendorFilter = false,
  onExportPdf,
  onExportExcel,
  exporting = false,
  historicalMode = false,
  onHistoricalModeChange,
}) {
  const [open, setOpen] = useState(false);
  const [vendorModalOpen, setVendorModalOpen] = useState(false);
  const panelRef = useRef(null);

  const selectedVendor = vendors.find(
    (vendor) => String(vendor.id) === String(vendorId)
  );

  useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event) {
      if (panelRef.current?.contains(event.target)) return;
      setOpen(false);
    }

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const hasActiveFilters = Boolean(dateFrom || dateTo || vendorId);

  const dateSummary = dateFrom || dateTo
    ? `${dateFrom ? dateFrom.toLocaleDateString() : '…'} – ${dateTo ? dateTo.toLocaleDateString() : '…'}`
    : labels.allDates || 'All dates';

  function clearFilters() {
    onDateFromChange(null);
    onDateToChange(null);
    if (onVendorChange) onVendorChange('');
  }

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-2">
        <div className={`relative ${open ? 'z-[10020]' : ''}`}>
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            className={`inline-flex items-center gap-2 rounded-xl border border-secondary-300/70 bg-white/80 px-3 py-2 text-sm font-medium text-secondary-700 shadow-soft backdrop-blur-sm transition-[border-color,background-color,box-shadow,transform] duration-200 ease-in-out hover:bg-white     ${
              open ? 'border-orange-300/70 shadow-medium ring-2 ring-orange-400/20  ' : ''
            }`}
          >
            <CalendarDaysIcon className="h-4 w-4 text-orange-500" />
            <span>{dateSummary}</span>
            <ChevronDownIcon className={`h-4 w-4 text-secondary-400 transition-transform duration-300 ease-in-out ${open ? 'rotate-180' : ''}`} />
          </button>

          <DropdownPanel
            open={open}
            className="w-[min(100vw-2rem,20rem)] p-4"
          >
            <div ref={panelRef}>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-secondary-500 ">
                {labels.dateRange || 'Date range'}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-sm">
                  <span className="mb-1 block text-secondary-600 ">
                    {labels.dateFrom || 'From'}
                  </span>
                  <DateInput
                    value={formatInputDate(dateFrom)}
                    onChange={(event) => onDateFromChange(parseInputDate(event.target.value))}
                    aria-label={labels.dateFrom || 'From'}
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-secondary-600 ">
                    {labels.dateTo || 'To'}
                  </span>
                  <DateInput
                    value={formatInputDate(dateTo)}
                    min={formatInputDate(dateFrom)}
                    onChange={(event) => onDateToChange(parseInputDate(event.target.value))}
                    aria-label={labels.dateTo || 'To'}
                  />
                </label>
              </div>
              <div className="mt-3 flex justify-end">
                <motion.button
                  type="button"
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setOpen(false)}
                  className="rounded-xl px-3 py-1.5 text-sm font-semibold text-orange-600 transition-colors hover:bg-orange-50 "
                >
                  {labels.apply || 'Apply'}
                </motion.button>
              </div>
            </div>
          </DropdownPanel>
        </div>

        {showVendorFilter ? (
          <>
            <button
              type="button"
              onClick={() => setVendorModalOpen(true)}
              aria-label={labels.filterByVendorButton || labels.vendor || 'Vendor'}
              className={`inline-flex max-w-full items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium shadow-soft backdrop-blur-sm transition-[border-color,background-color,box-shadow,transform] duration-200 ease-in-out ${
                vendorId
                  ? 'border-orange-300/70 bg-orange-50 text-orange-800 ring-2 ring-orange-400/20    '
                  : 'border-secondary-300/70 bg-white/80 text-secondary-700 hover:bg-white    '
              }`}
            >
              <FunnelIcon className={`h-4 w-4 shrink-0 ${vendorId ? 'text-orange-500' : 'text-secondary-400'}`} />
              <span className="truncate">
                {selectedVendor
                  ? selectedVendor.username
                  : (labels.filterByVendorButton || 'Filter by vendor')}
              </span>
            </button>

            <VendorFilterModal
              isOpen={vendorModalOpen}
              onClose={() => setVendorModalOpen(false)}
              vendors={vendors}
              selectedVendorId={vendorId}
              onSelect={onVendorChange}
              labels={labels}
            />
          </>
        ) : null}

        <button
          type="button"
          onClick={() => onHistoricalModeChange?.(!historicalMode)}
          aria-pressed={historicalMode}
          title={historicalMode ? (labels.historicalModeActiveHint || labels.historicalMode) : (labels.historicalModeHint || labels.historicalMode)}
          className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium shadow-soft backdrop-blur-sm transition-[border-color,background-color,box-shadow,transform] duration-200 ease-in-out ${
            historicalMode
              ? 'border-orange-300/70 bg-orange-50 text-orange-700 ring-2 ring-orange-400/20    '
              : 'border-secondary-300/70 bg-white/80 text-secondary-700 hover:bg-white    '
          }`}
        >
          <ClockIcon className={`h-4 w-4 ${historicalMode ? 'text-orange-500' : 'text-secondary-400'}`} />
          <span>{labels.historicalMode || 'Historical'}</span>
        </button>

        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-2 text-sm font-medium text-secondary-600 hover:bg-secondary-100  "
          >
            <XMarkIcon className="h-4 w-4" />
            {labels.clearFilters || 'Clear'}
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          size="small"
          disabled={exporting}
          onClick={onExportExcel}
          className="inline-flex items-center gap-1.5"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          {labels.exportExcel || 'Export Excel'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="small"
          disabled={exporting}
          onClick={onExportPdf}
          className="inline-flex items-center gap-1.5"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          {labels.exportPdf || 'Export PDF'}
        </Button>
      </div>
    </div>
  );
}

export default StateDistributionFilters;
